import React, { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  AlignLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

export function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Escreva algo...",
  maxLength,
  className,
}) {
  const containerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [alignIndex, setAlignIndex] = useState(0);
  const alignments = ["left", "center", "right", "justify"];
  const [charCount, setCharCount] = useState(() => stripHtml(value).length);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph", "listItem", "blockquote"],
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      let html = editor.getHTML();
      if (html === "<p></p>" || html === "<p><br></p>") {
        html = "";
      }
      const plain = stripHtml(html);
      setCharCount(plain.length);
      onChange?.(html);
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      if (value === "") {
        editor.commands.setContent("", true);
      } else {
        editor.commands.setContent(value);
      }
      setCharCount(stripHtml(value).length);
    }
  }, [value, editor]);

  // Click outside → blur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!editor) return null;

  const toggleAlignment = () => {
    const next = (alignIndex + 1) % alignments.length;
    setAlignIndex(next);
    editor.chain().focus().setTextAlign(alignments[next]).run();
  };

  const ToolbarButton = ({ onClick, active, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        "p-1.5 rounded transition-colors hover:bg-accent",
        active && "bg-accent text-accent-foreground"
      )}
    >
      {children}
    </button>
  );

  return (
    <div className={cn("space-y-1", className)}>
      <div
        ref={containerRef}
        className={cn(
          "border rounded-md overflow-hidden transition-colors",
          isFocused ? "ring-2 ring-ring border-input" : "border-input"
        )}
      >
        {/* Editor area */}
        <EditorContent
          editor={editor}
          className="rich-text-editor-content px-3 py-2 min-h-[160px] max-h-[300px] overflow-y-auto text-sm prose prose-sm max-w-none focus:outline-none"
          onFocus={() => {
            setIsFocused(true);
            editor.chain().focus().run();
          }}
        />

        {/* Toolbar at bottom (like candidate platform) */}
        <div className="flex items-center gap-0.5 px-2 py-1.5 border-t bg-muted/40">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            title="Negrito"
          >
            <Bold className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive("underline")}
            title="Sublinhado"
          >
            <UnderlineIcon className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive("strike")}
            title="Tachado"
          >
            <Strikethrough className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-5 bg-border mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            title="Lista"
          >
            <List className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleAlignment}
            active={false}
            title="Alinhamento"
          >
            <AlignLeft className="w-4 h-4" />
          </ToolbarButton>

          {maxLength && (
            <span
              className={cn(
                "ml-auto text-xs",
                charCount > maxLength ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function RichTextViewer({ content, className }) {
  if (!content) return <span className="text-muted-foreground">—</span>;

  return (
    <div
      className={cn(
        "prose prose-sm max-w-none text-sm",
        "[&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_h4]:text-base",
        "[&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4",
        "[&_p]:my-1",
        className
      )}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}
